/**
 * Linked Table Config Tab - displays linked table configuration
 *
 * Data Flow:
 * - Receives linked table details from PreviewSidePanel (parent) as a prop
 * - Parent has already fetched the detail data, avoiding duplicate API calls
 * - available_fields is now included in the detail endpoint response (from PostgreSQL)
 * - No separate preview query needed - all data comes from the parent prop
 * - If refetch is needed, use queryClient.invalidateQueries in parent
 */

import { useMemo } from 'react';
import {
  Table,
  Link2,
  FileText,
  Columns3,
} from 'lucide-react';
import {
  Skeleton,
  Badge,
} from '@loader/ui';
import { parseFieldId } from '@loader/shared-types';
import type { LinkedTableApiResponse } from '@loader/shared-types';

import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';

interface LinkedTableConfigTabProps {
  linkedTableDetails: LinkedTableApiResponse | undefined;
  isLoading?: boolean;
}

export function LinkedTableConfigTab({ linkedTableDetails, isLoading = false }: LinkedTableConfigTabProps) {
  const linkedTable = linkedTableDetails;

  // Build field map from available_fields included in detail response
  // This data comes from PostgreSQL (fast) instead of DuckDB preview
  const fieldMap = useMemo(() => {
    const map = new Map<string, { name: string; displayName: string; tableAlias: string; dataType: string }>();
    if (linkedTable?.available_fields) {
      for (const f of linkedTable.available_fields) {
        map.set(f.id, {
          name: f.name,
          displayName: f.name,
          tableAlias: f.table_alias,
          dataType: f.data_type,
        });
      }
    }
    // Also include calculated columns in the field map
    if (linkedTable?.calculated_columns) {
      for (const c of linkedTable.calculated_columns) {
        map.set(c.id, {
          name: c.name,
          displayName: c.name,
          tableAlias: '',
          dataType: c.data_type || 'number',
        });
      }
    }
    return map;
  }, [linkedTable?.available_fields, linkedTable?.calculated_columns]);

  const getFieldName = (fieldId: string): string => {
    // Field IDs may be qualified ("alias:uuid") — parse to extract bare UUID for lookup
    const parsed = parseFieldId(fieldId);
    const field = fieldMap.get(parsed.fieldId);
    return field?.displayName || field?.name || fieldId;
  };

  // Extract data from linkedTable (with defaults for when undefined)
  const sourceTables = linkedTable?.tables || [];
  const joins = linkedTable?.joins || [];
  const filters = linkedTable?.filters || [];
  const rowGroups = useMemo(() => linkedTable?.row_groups || [], [linkedTable?.row_groups]);
  const values = useMemo(() => linkedTable?.values || [], [linkedTable?.values]);

  // Build combined columns list: row groups + values
  // Must be called before early returns (Rules of Hooks)
  const columns = useMemo(() => {
    const cols: Array<{ id: string; name: string; dataType: string }> = [];

    // Add row groups (dimensions)
    // Field IDs may be qualified ("alias:uuid") — parse to extract bare UUID for lookup
    for (const fieldId of rowGroups) {
      const parsed = parseFieldId(fieldId);
      const field = fieldMap.get(parsed.fieldId);
      cols.push({
        id: fieldId,
        name: field?.name || fieldId,
        dataType: field?.dataType || 'string',
      });
    }

    // Add values (aggregations) formatted as FUNC(field_name)
    for (const val of values) {
      const parsed = parseFieldId(val.field_id);
      const field = fieldMap.get(parsed.fieldId);
      const fieldName = field?.name || val.field_id;
      cols.push({
        id: val.field_id,
        name: `${val.function.toUpperCase()}(${fieldName})`,
        dataType: 'number',
      });
    }

    return cols;
  }, [rowGroups, values, fieldMap]);

  // Early returns after all hooks
  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!linkedTable) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        Failed to load configuration
      </div>
    );
  }

  const hasNoConfig =
    joins.length === 0 &&
    filters.length === 0 &&
    rowGroups.length === 0 &&
    values.length === 0;

  if (hasNoConfig) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <Link2 className="size-8 opacity-50" />
        <span>No configuration defined</span>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4 overflow-hidden py-2">
      {/* Columns (Row Groups + Values) */}
      {columns.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Columns3 className="text-muted-foreground size-4" />
            <span className="text-sm font-medium">Columns</span>
            <Badge variant="secondary" className="text-xs">{columns.length}</Badge>
          </div>
          <div className="space-y-1">
            {columns.map((col, index) => {
              const TypeIcon = getTypeIcon(col.dataType);
              return (
                <div key={index} className="flex min-w-0 items-center gap-2 rounded-md border px-3 py-2">
                  {TypeIcon && <TypeIcon className="text-muted-foreground size-4 shrink-0" />}
                  <span className="w-0 flex-1 truncate text-sm">{col.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Source Tables */}
      {sourceTables.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Table className="text-muted-foreground size-4" />
            <span className="text-sm font-medium">Source Tables</span>
            <Badge variant="secondary" className="text-xs">{sourceTables.length}</Badge>
          </div>
          <div className="space-y-1">
            {sourceTables.map((source, index) => (
              <div key={source.id} className="flex min-w-0 items-center gap-2 rounded-md border px-3 py-2">
                <span className="w-0 flex-1 truncate text-sm">{source.alias}</span>
                <Badge variant="outline" className="shrink-0 text-xs">
                  {source.type === 'linked_table' ? 'Linked Table' : 'Table'}
                </Badge>
                {index === 0 && <Badge variant="secondary" className="shrink-0 text-xs">Primary</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Joins */}
      {joins.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Link2 className="text-muted-foreground size-4" />
            <span className="text-sm font-medium">Relationships</span>
            <Badge variant="secondary" className="text-xs">{joins.length}</Badge>
          </div>
          <div className="space-y-1">
            {joins.map((join) => (
              <div key={join.id} className="flex min-w-0 items-center gap-2 rounded-md border px-3 py-2">
                <span className="w-0 flex-1 truncate text-sm">{join.left_table} × {join.right_table}</span>
                <Badge variant="outline" className="shrink-0 text-xs">{join.join_type}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {filters.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <FileText className="text-muted-foreground size-4" />
            <span className="text-sm font-medium">Filters</span>
            <Badge variant="secondary" className="text-xs">{filters.length}</Badge>
          </div>
          <div className="space-y-1">
            {filters.map((filter) => {
              // field_name already includes table prefix (e.g., "sales.region")
              const displayName = filter.field_name || getFieldName(filter.field_id);
              return (
                <div key={filter.id} className="flex min-w-0 items-center gap-2 rounded-md border px-3 py-2">
                  <span className="w-0 flex-1 truncate text-sm">{displayName}</span>
                  <Badge variant="outline" className="shrink-0 text-xs">{filter.operator.replace(/_/g, ' ')}</Badge>
                  {filter.value !== null && (
                    <span className="text-muted-foreground shrink-0 truncate text-xs max-w-20">
                      {Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
